import {
  appendPostOfTopic,
  fetchTopicData,
} from "../../redux/features/topicsSlice";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PostDetailItem from "./PostDetailItem";
import "react-quill/dist/quill.snow.css";
import PostItem from "../Home/PostItem";
import moment from "moment";
import axios from "axios";
import { showCustomToast } from "../../utils/toastUtil";
import { getQuillModules, imageHandler } from "../../utils/quillUtil";
import { CustomReactQuill } from "../Editor/CustomReactQuill";
import { debounce } from "lodash";
import PostDetailsShimmer from "../partials/postDetailsShimmer";
import PostShimmer from "../partials/postShimmer";
import { ShimmerBadge } from "react-shimmer-effects";
import NotFound from "../NotFound";

const PostDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // =======================
  // useLocation
  // =======================
  const location = useLocation();
  let reply_mode = !!location?.state?.reply_mode;

  // =======================
  // Redux State Management
  // =======================
  const {
    topicPosts,
    topicDetails,
    suggestedTopics,
    status: postsStatus,
  } = useSelector((state) => state.topics);

  const post = topicDetails.mainPost || {};
  const filteredTopicPosts = topicPosts.filter(
    (post) => !post.reply_to_post_number
  );
  const sortedByAcceptedTopics = [...filteredTopicPosts].sort(
    (a, b) => b.accepted_answer - a.accepted_answer
  );

  // =======================
  // useParams
  // =======================
  const { topicId, postNumber } = useParams();

  // =======================
  // useRefs
  // =======================
  const quillRef = useRef(null);
  const postRefs = useRef({});
  const modalRef = useRef(null);
  const observerRef = useRef(null);

  // =======================
  // useStates
  // =======================
  // eslint-disable-next-line no-unused-vars
  const [formVisible, setFormVisible] = useState(reply_mode);
  const [reported, setReported] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState({});
  const [lastVisiblePost, setLastVisiblePost] = useState(0);
  const [replyToPostNumber, setReplyToPostNumber] = useState(null);
  const [intersectingPosts, setIntersectingPosts] = useState([]);

  // =======================
  // Admin Authentication
  // =======================
  const userData = localStorage.getItem("salla_discourse_user");
  const loggedUser = JSON.parse(userData);
  let isLoggedIn = false;
  if (loggedUser) {
    isLoggedIn = true;
  }

  // =======================
  // useEffects
  // =======================
  useEffect(() => {
    if (intersectingPosts.length > 0) {
      // Sort intersecting posts by their vertical position in the viewport
      const sortedPosts = [...intersectingPosts].sort((a, b) => {
        const rectA = a.element.getBoundingClientRect();
        const rectB = b.element.getBoundingClientRect();
        return rectA.top - rectB.top; // Sort by top position
      });

      // The topmost visible post is the first one in the sorted list
      const topPost = sortedPosts[0];
      setLastVisiblePost(topPost.postNumber);
      setCurrentTime(topPost.postDate);
    }
  }, [intersectingPosts]);

  // Initialize the Intersection Observer
  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null, // Use the viewport as the root
      rootMargin: "-90px",
      threshold: 0.7,
    });

    // Attach the observer to each post
    Object.values(postRefs.current).forEach((el) => {
      if (el) observerRef.current.observe(el);
    });

    // Cleanup the observer on unmount
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedByAcceptedTopics]);

  useEffect(() => {
    window.addEventListener(
      "scroll",
      debounce(() => {
        if (window.scrollY === 0) {
          setLastVisiblePost(0);
          setCurrentTime(topicDetails?.mainPost?.created_at);
        }
      }, 50)
    );
    dispatch(fetchTopicData(topicId, postNumber));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, topicId, postNumber]);

  const handleClose = useCallback(() => {
    setFormVisible(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose]);
  useEffect(() => {
    if (postsStatus === "succeeded" && postNumber) {
      const postIndex = Number(postNumber);
      if (postRefs.current[postIndex]) {
        postRefs.current[postIndex].classList.add("scroll-target"); // Apply scroll-margin-top

        postRefs.current[postIndex].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [postsStatus, postNumber]);

  // =======================
  // quill module
  // =======================
  const modules = useMemo(
    () => getQuillModules(quillRef, imageHandler),
    [quillRef]
  );

  // =======================
  // handlers
  // =======================

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleIntersection = useCallback(
    debounce((entries) => {
      entries.forEach((entry) => {
        const postNumber = entry.target.getAttribute("data-post-number");
        const postDate = entry.target.getAttribute("data-post-date");

        if (entry.isIntersecting) {
          setIntersectingPosts((prev) => [
            ...prev,
            { postNumber, postDate, element: entry.target },
          ]);
        } else {
          setIntersectingPosts((prev) =>
            prev.filter((post) => post.postNumber !== postNumber)
          );
        }
      });
    }, 50), // Adjust the delay as needed
    []
  );

  const incrementCount = async (id) => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/t/${id}/increment_count`;

      await axios.get(apiUrl, null);
    } catch (error) {
      console.error("Increment count failed:", error);
    }
  };
  useEffect(() => {
    if (topicId) {
      incrementCount(topicId);
    }
  }, [topicId]);
  async function handleReport(id) {
    setReported(true);

    const formData = new FormData();
    formData.append("flag_topic", false);
    formData.append("post_action_type_id", 8);
    formData.append("id", id);

    const userObj = localStorage.getItem("salla_discourse_user");
    const headers = {};

    if (userObj) {
      const user = JSON.parse(userObj);
      headers["Api-Username"] = user?.id;
      headers["Api-Key"] = user?.api_key;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/post_actions`,
        {
          method: "POST",
          body: formData,
          headers,
        }
      );

      if (response.ok) {
        showCustomToast("تم الإبلاغ عن المنشور بنجاح", "success");
      } else if (response.status === 403) {
        showCustomToast("لقد قمت بالإبلاغ عن هذا المنشور من قبل ", "error");
      }
    } catch (error) {
      console.error("Error reporting post:", error);
    }
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const editor = quillRef.current.getEditor();
    let value = editor.root.innerHTML;
    if (!value.trim() || value === "<p><br></p>") {
      showCustomToast("تحتاج إلى تقديم المحتوى أولاً", "error");
      setLoading(false);
      return;
    }
    const formData = new FormData();
    const { id, category_id } = topicDetails;
    formData.append("raw", value);
    formData.append("category", category_id);
    formData.append("topic_id", id);
    formData.append("nested_post", "true");

    if (replyToPostNumber) {
      formData.append("reply_to_post_number", replyToPostNumber);
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/posts`,
        formData
      );
      const { data } = response;
      if (data.success) {
        if (replyToPostNumber) {
          dispatch(fetchTopicData(topicId, postNumber));
        } else {
          dispatch(appendPostOfTopic(response?.data?.post));
        }
        setFormVisible(false);
        editor.setText("");
        setErrors([]);
      } else {
        setErrors(response?.errors);
        console.error("Failed to post:", response.statusText);
      }
      setLoading(false);
    } catch (error) {
      setErrors(error.response.data.errors);
      console.error("Error:", error);
      setLoading(false);
    } finally {
      setReplyToPostNumber(null);
      setLoading(false);
    }
  }

  if (postsStatus === "loading" || loading) {
    return (
      <div dir="rtl" className="flex items-start w-full p-6">
        <div className="flex flex-col gap-y-2 w-full min-post:w-5/6 xl:!w-3/4">
          {
            <>
              <PostDetailsShimmer></PostDetailsShimmer>

              <div className="mt-[50px] mb-8 flex gap-5 items-center">
                <hr className="flex-grow mb-5" />
                <ShimmerBadge></ShimmerBadge>
                <hr className="flex-grow mb-5" />
              </div>

              {[...Array(6)].map((_, index) => (
                <PostShimmer key={index}></PostShimmer>
              ))}
            </>
          }
        </div>
        <div className="lg:flex hidden gap-3 w-1/4 justify-center">
          <div className="h-[300px] shimmer min-w-2 rounded-full"></div>
          <div className="flex flex-col justify-between h-[300px]">
            <div className="shimmer rounded-xl h-[13px] w-[80px]"></div>
            <div className="shimmer rounded-xl h-[13px] w-[60px]"></div>
          </div>
        </div>
      </div>
    );
  }
  const isStillLoading = postsStatus === "loading" || loading;

  if (!isStillLoading && (!topicDetails || !post?.id)) {
    return <NotFound />;
  }

  return (
    <div className="flex flex-col space-x-8 justify-end w-full">
      <div className="py-6 sm:px-6 px-4 pt-0 w-full" dir="rtl">
        <div className="flex">
          <div
            className="posts-container mt-6  w-full min-post:w-5/6 xl:!w-3/4"
            style={{ display: "inline-block", verticalAlign: "top" }}
          >
            <div className="flex justify-between">
              <div className="flex items-center">
                <img
                  src="/images/post/go-back.svg"
                  alt="back"
                  className="p-3 max-sm:ps-0 cursor-pointer"
                  onClick={() =>
                    navigate(`/category-detail/${post?.category.id}`)
                  }
                />
                <p className="w-auto h-auto text-[18px] text-[#444444] flex font-bold">
                  {post?.category?.emoji && (
                    <img
                      src={post?.category?.emoji}
                      className="max-h-[18px] me-1"
                      alt=""
                    />
                  )}
                  {post?.category?.name}
                </p>
              </div>
              {!post?.yours && isLoggedIn && !reported ? (
                <button
                  onClick={() => handleReport(post?.id)}
                  className="flex gap-2 items-center hover:bg-red-100 px-2 rounded-xl"
                >
                  <img src="/images/post/post-report.svg" alt="rep" />
                  <p className="text-right text-[#999999] text-xs font-medium">
                    إبلاغ عن مخالفة
                  </p>
                </button>
              ) : null}
            </div>
            <PostDetailItem
              topicId={topicId}
              post={post}
              answer={
                topicDetails.accepted_answer ? sortedByAcceptedTopics[0] : null
              }
              topicDetails={topicDetails}
              isTopic={true}
              showReplyForm={() => {
                setReplyToPostNumber(post?.post_number);
                setFormVisible(true);
              }}
            />
            {topicPosts?.length > 0 && (
              <div className="mt-[50px] mb-8 flex gap-5 items-center">
                <hr className="flex-grow" />
                <span className="text-[#666666] text-lg font-medium">
                  التعليقات ({topicPosts?.length})
                </span>
                <hr className="flex-grow" />
              </div>
            )}
            {sortedByAcceptedTopics.map((post, index) => {
              return (
                <div
                  key={post.id}
                  ref={(el) => (postRefs.current[post.post_number] = el)}
                  data-post-number={index + 1}
                  data-post-date={post.created_at}
                >
                  <PostDetailItem
                    key={post.id}
                    topicId={topicId}
                    post={post}
                    postNumber={post?.post_number}
                    topicDetails={topicDetails}
                    isTopic={false}
                    showReplyForm={() => {
                      setReplyToPostNumber(post?.post_number);
                      setFormVisible(true);
                    }}
                    // index={index}
                  />
                </div>
              );
            })}
          </div>
          {/* post timeline */}
          <div className="min-post:block hidden xl:w-1/4 w-1/6">
            <div className="xl:p-[60px_60px_0px_80px] p-[60px_40px_0px_20px] max-h-fit sticky top-16">
              <span
                className="cursor-pointer text-center text-[#666666] text-base font-medium"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {moment(post.created_at).locale("ar").format("D MMMM")}
              </span>
              <div className="h-[300px] max-w-[2px] bg-[#BAF3E6] my-4 me-3">
                <div
                  className="w-2 h-14 bg-[#76E8CD] relative -ms-[3px] transition-transform"
                  style={{
                    transform: `translateY(${Math.max(
                      lastVisiblePost *
                        (300 * (1 / sortedByAcceptedTopics.length)) -
                        56,
                      0
                    )}px)`,
                  }}
                >
                  <div className="absolute w-max start-5">
                    <p className="text-right text-[#333333] text-lg font-medium">
                      {lastVisiblePost}/{sortedByAcceptedTopics.length}
                    </p>
                    <p className="text-center text-[#666666] text-base font-normal">
                      {moment(currentTime).locale("ar").format("D MMMM")}
                    </p>
                  </div>
                </div>
              </div>
              <span
                className="cursor-pointer text-center text-[#666666] text-base font-medium"
                onClick={() => {
                  const targetDiv = document.querySelector(
                    `[data-post-number="${sortedByAcceptedTopics.length}"]`
                  );
                  targetDiv.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }}
              >
                {moment(post.last_posted_at).locale("ar").format("D MMMM")}
              </span>
            </div>
          </div>
        </div>

        {isLoggedIn ? (
          <>
            <div className="flex items-center gap-2 mt-10 mb-3">
              <img src="/images/post/post-addReply.svg" alt="fowroard" />
              <p className="text-[#666666] text-sm font-medium">إضافة تعليق</p>
            </div>
            <div className="flex flex-col min-post:w-5/6 xl:!w-3/4 w-full">
              <style>
                {`
                .ql-container {
                  height: 256px !important;
                }
              `}
              </style>
              <div className="w-full">
                <form className="flex flex-col" onSubmit={handleFormSubmit}>
                  <div className="shadow-md rounded-[10px] bg-white ">
                    {errors?.map((error, index) => (
                      <span key={index} className="text-red-500 block">
                        {error}
                      </span>
                    ))}

                    <CustomReactQuill
                      quillRef={quillRef}
                      modules={modules}
                      placeholder="أضف تعليقك على هذا المنشور 🖊️"
                    />
                  </div>

                  <div className="flex space-x-4 pt-4 w-fit self-end">
                    <button
                      disabled={loading}
                      type="submit"
                      className="bg-[#004D5A] flex gap-2 flex-grow-0 px-9 py-3 rounded-[4px] text-white text-medium text-[16px]"
                    >
                      {loading ? (
                        <img
                          loading="eager"
                          src="/images/salla_loader_big.svg"
                          alt="Loading..."
                          className="w-[12px] h-[15px] loader"
                        />
                      ) : (
                        <img
                          src="/images/post/post-sendReply.svg"
                          alt="reply"
                        />
                      )}
                      {loading ? "الرجاء الانتظار..." : "نشر التعليق"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center w-full min-post:w-5/6 xl:!w-3/4 my-16 gap-4">
            <p className="text-[#666666] text-sm font-medium">
              سجّل دخولك لتتمكن من الرد
            </p>{" "}
            <a
              href={process.env.REACT_APP_API_URL + `/auth/oauth2_basic`}
              className="flex but but--medium bg-[#004D5A] hover:bg-[#003E4A]"
            >
              <p className="text-secondary-25 text-medium text-[16px]">
                تسجيل الدخول
              </p>
            </a>
          </div>
        )}
        {suggestedTopics && suggestedTopics.length > 0 && (
          <div className="mt-[6rem] w-full min-post:w-5/6 xl:!w-3/4">
            <span className="text-[#333333] text-lg font-medium">
              منشورات قد تهمك
            </span>
            <div className="mt-[2rem]">
              {suggestedTopics.map((topic) => (
                <React.Fragment key={topic.id}>
                  <PostItem
                    post={topic}
                    isLoggedIn={isLoggedIn}
                    loggedInUserId={loggedUser?.id}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDetails;
